<template>
    <pc-table
        v-bind:id="id"
        v-bind:items="forms"
        v-bind:columns="columns"
        v-bind:initial-sort="initialSort"
        v-bind:empty-text="emptyText"
        key-prop="FormId"
        hide-search
        card-hide-headers>
        <template v-slot:Description="form">
            <div class="d-flex align-items-center">
                <div
                    data-test-role="form-name"
                    class="flex-grow-1">
                    {{ form.Description }}
                    <div
                        v-if="form.IsFirstSubmittedForProject"
                        class="d-inline mx-1"
                        v-bind:title="firstSubmittedFormMessage">
                        <pc-bs-icon
                            v-if="form.IsFirstSubmittedForProject"
                            name="1-circle" />
                    </div>
                </div>
                <div v-bind:class="buttonRowClass">
                    <pc-button-row>
                        <template v-slot:right>
                            <pc-button
                                v-if="canEditProjectForms && !form.HasBeenOpened"
                                class="form-action-button"
                                data-test-role="begin-button"
                                small
                                v-on:click="$emit('edit-project-form', form.FormId)">
                                {{ beginFormButtonLabel }}
                            </pc-button>
                            <pc-button
                                v-if="canEditProjectForms && form.HasBeenOpened && form.IsDraft"
                                class="form-action-button"
                                data-test-role="continue-button"
                                small
                                v-on:click="$emit('edit-project-form', form.FormId)">
                                {{ continueFormButtomLabel }}
                            </pc-button>
                            <pc-button
                                v-if="canEditProjectForms && form.HasBeenOpened && !form.IsDraft"
                                class="form-action-button"
                                data-test-role="edit-button"
                                small
                                v-on:click="$emit('edit-project-form', form.FormId)">
                                {{ editFormButtonLabel }}
                            </pc-button>
                            <pc-button
                                v-if="form.HasBeenOpened"
                                class="form-action-button"
                                data-test-role="view-button"
                                small
                                v-on:click="$emit('view-project-form', form.FormId)">
                                {{ viewFormButtonLabel }}
                            </pc-button>
                        </template>
                    </pc-button-row>
                    <div
                        v-if="!canEditProjectForms && !form.HasBeenOpened"
                        class="form-action-text">
                        {{ noDataFormText }}
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:FormStatus="form">
            <div
                data-test-role="form-status"
                class="fw-bold">
                {{ form.FormStatus }}
            </div>
            <div data-test-role="form-status-description">
                {{ form.FormStatusDescription }}
            </div>
        </template>
    </pc-table>
</template>

<script setup>
    import { inject, computed } from "vue";
    import { TABLE_CARD_BP } from "Components/table/pc-table.vue";
    import { isAtLeast } from "Responsive/manager";
    import { KEYS, NAMESPACE } from "./i18n/projectFormTable.i18n";
    import { useResource } from "Util/i18n";

    defineEmits(["edit-project-form", "view-project-form"]);

    const props = defineProps({
        forms: { type: Object, required: true },
        canEditProject: { type: Boolean, required: true },
        emptyTableText: { type: String, required: false, default: null },
        formsAreViewOnly: { type: Boolean, required: false, default: false },
        id: { type: String, required: false, default: null },
        initialSort: {
            type: Array,
            required: false,
            // By default, sort by "Sort Order" desc then "Description" asc
            default: () => [
                [2, "desc"],
                [0, "asc"]
            ]
        }
    });

    const {
        beginFormButtonLabel,
        continueFormButtomLabel,
        editFormButtonLabel,
        emptyFormsTableText,
        firstSubmittedFormMessage,
        noDataFormText,
        tableDescriptionColumnHeader,
        tableStatusColumnHeader,
        viewFormButtonLabel
    } = useResource(KEYS, NAMESPACE);
    const emptyText = props.emptyTableText ?? emptyFormsTableText.value;

    const columns = [
        {
            header: tableDescriptionColumnHeader.value,
            property: "Description",
            orderable: true,
            visible: true,
            columnClass: "align-middle"
        },
        {
            header: tableStatusColumnHeader.value,
            property: "FormStatus",
            orderable: true,
            visible: true,
            columnClass: "align-middle"
        },
        {
            // This column refers to the order configured in the FormList page
            // It's the default sorting in the legacy capsule, but it's also an invisible
            // column so it's not clear what the header should be. In responsive, users
            // will see the header in card mode as one of the sort options even though
            // the column is invisible
            header: "Form Order",
            property: "SortOrder",
            orderable: true
        }
    ];

    const canEditProjectForms = computed(() => {
        return !props.formsAreViewOnly && props.canEditProject;
    });

    const viewport = inject("viewport", null);
    const buttonRowClass = computed(() => {
        const classes = ["flex-shrink-0", "text-end"];
        if (isAtLeast(viewport, TABLE_CARD_BP)) {
            classes.push("me-3");
            classes.push("my-2");
        }
        return classes;
    });
</script>

<style lang="scss" scoped>
    .form-action-button {
        min-width: 5rem !important;
    }
    .form-action-text {
        min-width: 5rem !important;
        display: inline-block;
        opacity: 0.65;
        text-align: center;
        font-size: 0.875rem;
    }
</style>
